import { create } from "zustand";

// Backs the soft "connection issue" banner (NetworkHealthBanner.tsx) and the
// decision to escalate to a full /maintenance redirect (axios.ts). Previously
// ANY response-less axios error (a single CORS/timeout blip included)
// redirected the whole SPA to /maintenance immediately — a new user's very
// first session could hit that from one transient hiccup and look "broken"
// instead of just briefly degraded.
//
// Escalation rule: a burst of requests failing at the same instant (e.g. 5
// parallel calls on page load, all hit by the same blip) must NOT immediately
// look like a sustained outage — so escalation requires BOTH a minimum
// failure count AND that the streak is still unresolved after a short grace
// window, not just "N failures", however close together they land. Any
// success within the grace window cancels the escalation and clears the
// streak entirely.
const FAILURE_THRESHOLD = 3;
const GRACE_WINDOW_MS = 5000;

interface NetworkHealthState {
  status: "ok" | "degraded";
}

export const useNetworkHealthStore = create<NetworkHealthState>(() => ({
  status: "ok",
}));

let firstFailureAt: number | null = null;
let failureCount = 0;
let escalationTimer: ReturnType<typeof setTimeout> | null = null;

const clearStreak = () => {
  firstFailureAt = null;
  failureCount = 0;
  if (escalationTimer) {
    clearTimeout(escalationTimer);
    escalationTimer = null;
  }
};

export const reportNetworkSuccess = () => {
  if (failureCount === 0) return;
  clearStreak();
  useNetworkHealthStore.setState({ status: "ok" });
};

// onEscalate is passed in (rather than imported/called directly) so this
// module stays test/reuse-friendly and axios.ts stays the single place that
// decides what "escalate" actually means (redirecting to /maintenance).
export const reportNetworkFailure = (onEscalate: () => void) => {
  if (firstFailureAt === null) firstFailureAt = Date.now();
  failureCount += 1;
  useNetworkHealthStore.setState({ status: "degraded" });

  if (escalationTimer) return; // already waiting out the grace window
  escalationTimer = setTimeout(() => {
    escalationTimer = null;
    if (failureCount >= FAILURE_THRESHOLD) {
      onEscalate();
    }
    // Below threshold when the window closes — a real but minor blip, not
    // sustained. Leave the streak as-is: a NEW failure after this point
    // starts its own fresh grace window rather than escalating instantly.
    firstFailureAt = null;
    failureCount = 0;
  }, GRACE_WINDOW_MS);
};
